import { useSelector as useReduxSelector } from 'react-redux'

import { IAppState } from '~/shared/store/types'

type TSelector<T> = (state: IAppState) => T
type TEqualityFn<T> = (prev: T, next: T) => boolean

type TSelectorConfig<T> = {
  select: TSelector<T>
  equalityFn: TEqualityFn<T>
}

type TSelectorOrConfig<T> = TSelector<T> | TSelectorConfig<T>

type TSelectorMap = Record<string, TSelectorOrConfig<any>>

type TInferSelectorMapReturn<T extends TSelectorMap> = {
  [K in keyof T]: T[K] extends TSelectorConfig<infer R>
    ? R
    : T[K] extends TSelector<infer R>
    ? R
    : never
}

export const useBulkSelector = <T extends TSelectorMap>(
  selectors: T,
): TInferSelectorMapReturn<T> => {
  return useReduxSelector(
    (state: IAppState) => {
      const selectorEntries = Object.entries(selectors)

      return selectorEntries.reduce(
        (results, [selectorKey, selectorOrConfig]) => {
          const selectorFn =
            typeof selectorOrConfig === 'function'
              ? selectorOrConfig
              : selectorOrConfig.select

          const currentValue = selectorFn(state)

          return {
            ...results,
            [selectorKey]: currentValue,
          }
        },
        {} as TInferSelectorMapReturn<T>,
      )
    },
    (prev, next) => {
      return Object.entries(selectors).every(([key, selectorOrConfig]) => {
        const equalityFn =
          typeof selectorOrConfig === 'function'
            ? undefined
            : selectorOrConfig.equalityFn

        return equalityFn
          ? equalityFn(prev[key], next[key])
          : prev[key] === next[key]
      })
    },
  )
}
